/**
 * The web reads the SAME token module the app does (src/theme/tokens.ts is pure data —
 * its only react-native import is a type, which erases at compile). Design parity is
 * therefore structural: change a token and both clients move. Nothing here invents a
 * value.
 */
import { colors, spacing, radii, type as typeScale, clay, pastelFor } from '@/theme/tokens';

const kebab = (s: string): string => s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

export function cssVariables(): string {
  const lines: string[] = [];
  for (const [key, value] of Object.entries(colors)) lines.push(`  --color-${kebab(key)}: ${value};`);
  for (const [key, value] of Object.entries(spacing)) lines.push(`  --space-${key}: ${value}px;`);
  for (const [key, value] of Object.entries(radii)) lines.push(`  --radius-${key}: ${value}px;`);
  for (const [key, value] of Object.entries(typeScale)) {
    lines.push(`  --font-size-${key}: ${value.fontSize}px;`);
    lines.push(`  --font-weight-${key}: ${value.fontWeight};`);
    if ('letterSpacing' in value && value.letterSpacing !== undefined) {
      lines.push(`  --letter-spacing-${key}: ${value.letterSpacing}px;`);
    }
  }
  return `:root {\n${lines.join('\n')}\n}\n`;
}

/** The clay signature: hard offset, zero blur. Mirrors clayShadow() in the app. */
export function clayShadowCss(kind: keyof typeof clay, pressed = false): string {
  const { color, offsetY } = clay[kind];
  const y = pressed ? Math.max(offsetY - 2, 1) : offsetY;
  return `0 ${y}px 0 ${color}`;
}

export function pastelStyle(name: string): { background: string; color: string } {
  const pair = pastelFor(name);
  return { background: pair.bg, color: pair.fg };
}
